import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { sponsorsService } from '../../services/sponsors';
import UploadFileButton from './UploadFileButton';
import type { Sponsor } from '../../types/sponsor';

/** The server's limits (Sponsor::LOGO_*), checked here before an upload. */
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/avif';
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

interface SponsorFormDialogProps {
  slug: string;
  /** The sponsor being edited, or null for a new one. */
  sponsor: Sponsor | null;
  onSaved: (sponsor: Sponsor) => void;
  onClose: () => void;
}

/**
 * Adds a sponsor or edits one: its logo, its name and, optionally, the link the
 * logo leads to. A new sponsor needs a logo — the strip shows nothing else.
 */
export default function SponsorFormDialog({ slug, sponsor, onSaved, onClose }: SponsorFormDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [name, setName] = useState(sponsor?.name ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(sponsor?.website_url ?? '');
  const [signedId, setSignedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(sponsor?.logo_small_url ?? null);
  const [saving, setSaving] = useState(false);

  // The picked file's preview lives as long as it is shown.
  useEffect(() => {
    return () => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const canSave = name.trim() !== '' && (!!sponsor || !!signedId) && !saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      const input = { name: name.trim(), website_url: websiteUrl.trim(), ...(signedId ? { signed_id: signedId } : {}) };
      const saved = sponsor
        ? await sponsorsService.update(slug, sponsor.id, input)
        : await sponsorsService.create(slug, input);
      onSaved(saved);
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(sponsor ? 'gymAdmin.sponsors.editTitle' : 'gymAdmin.sponsors.addTitle')}</DialogTitle>
          <DialogDescription>{t('gymAdmin.sponsors.formHelp')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('gymAdmin.sponsors.logo')}</Label>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-28 items-center justify-center rounded border bg-white p-1">
                {preview && <img src={preview} alt="" className="max-h-full max-w-full object-contain" />}
              </div>
              <UploadFileButton
                accept={LOGO_ACCEPT}
                maxBytes={LOGO_MAX_BYTES}
                testId="sponsor-logo-input"
                onUploaded={(id, file) => {
                  setSignedId(id);
                  setPreview(URL.createObjectURL(file));
                }}
                onError={(reason) =>
                  toast({
                    title: t(reason === 'too_large' ? 'gymAdmin.sponsors.logoTooLarge' : 'gymAdmin.sponsors.uploadError'),
                    variant: 'destructive',
                  })
                }
              >
                {t(preview ? 'gymAdmin.sponsors.replaceLogo' : 'gymAdmin.sponsors.pickLogo')}
              </UploadFileButton>
            </div>
            <p className="text-xs text-muted-foreground">{t('gymAdmin.sponsors.logoHelp')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sponsor-name">{t('gymAdmin.sponsors.name')}</Label>
            <Input id="sponsor-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sponsor-website">{t('gymAdmin.sponsors.website')}</Label>
            <Input
              id="sponsor-website"
              type="url"
              inputMode="url"
              placeholder="https://"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
